import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IncomeStreamExclusionReason } from 'src/app/models/Argyle';

export type StreamAction = 'include' | 'exclude';

export interface IncludeDecision {
  action: 'include';
  monthlyAmount: number;
  notes: string | null;
}

export interface ExcludeDecision {
  action: 'exclude';
  exclusionReasonId: number;
  notes: string | null;
}

export type PanelDecision = IncludeDecision | ExcludeDecision;

/** State object representing the draft's current decision. Pass null/undefined for a fresh panel. */
export interface PanelDraftState {
  isExcluded: boolean;
  incomeAmount: number;
  exclusionReasonId: number | null;
  notes: string | null;
}

@Component({
  selector: 'app-stream-review-question-panel',
  templateUrl: './stream-review-question-panel.component.html',
  styleUrls: ['./stream-review-question-panel.component.less'],
})
export class StreamReviewQuestionPanelComponent implements OnChanges {
  @Input() draftState: PanelDraftState | null = null;
  @Input() suggestedAmount: number | null = null;
  @Input() exclusionReasons: IncomeStreamExclusionReason[] = [];

  @Output() decision = new EventEmitter<PanelDecision>();

  selectedAction: StreamAction | null = null;
  monthlyAmount: number | null = null;
  notes = '';
  selectedReasonId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['draftState'] || changes['suggestedAmount']) {
      this.reset();
    }
  }

  selectAction(action: StreamAction): void {
    this.selectedAction = action;
  }

  get canConfirmInclude(): boolean {
    return this.monthlyAmount != null && this.monthlyAmount > 0;
  }

  get canConfirmExclude(): boolean {
    return this.selectedReasonId != null;
  }

  onConfirm(): void {
    const trimmedNotes = this.notes?.trim() || null;

    if (this.selectedAction === 'include' && this.canConfirmInclude) {
      this.decision.emit({
        action: 'include',
        monthlyAmount: Math.round(this.monthlyAmount! * 100) / 100,
        notes: trimmedNotes,
      });
      return;
    }

    if (this.selectedAction === 'exclude' && this.canConfirmExclude) {
      this.decision.emit({
        action: 'exclude',
        exclusionReasonId: this.selectedReasonId!,
        notes: trimmedNotes,
      });
    }
  }

  private reset(): void {
    if (this.draftState) {
      // Populate from existing draft state (editing a reviewed draft).
      this.selectedAction = this.draftState.isExcluded ? 'exclude' : 'include';
      this.monthlyAmount = this.draftState.isExcluded ? null : this.draftState.incomeAmount;
      this.selectedReasonId = this.draftState.exclusionReasonId;
      this.notes = this.draftState.notes ?? '';
    } else {
      // Fresh panel — pre-fill suggested amount if provided.
      this.selectedAction = null;
      this.monthlyAmount = this.suggestedAmount;
      this.notes = '';
      this.selectedReasonId = null;
    }
  }
}
